// ═══════════════════════════════════════════════════════════════════════════════
// FILE: src/app/core/interceptors/auth-error.interceptor.ts
// VERSION: v1.0.0 - Authentication Error Interceptor
// DATE: 2025-01-09
//
// PURPOSE: Global HTTP error handler for 401/403 responses
// TRIGGERS: Auth Error Dialog → 5 second countdown → Redirect to login
// ═══════════════════════════════════════════════════════════════════════════════

import { Injectable } from '@angular/core';
import { 
  HttpInterceptor, 
  HttpRequest, 
  HttpHandler, 
  HttpEvent, 
  HttpErrorResponse 
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { 
  AuthErrorDialogComponent, 
  AuthErrorDialogData 
} from '../shared/dialogs/auth-error-dialog/auth-error-dialog.component';

@Injectable()
export class AuthErrorInterceptor implements HttpInterceptor {
  
  private dialogOpen = false; // Prevent multiple dialogs
  
  constructor(private dialog: MatDialog) {}
  
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        
        // Only handle authentication errors
        if (error.status === 401 || error.status === 403) {
          console.error('[AuthErrorInterceptor] Authentication error detected:', {
            status: error.status,
            message: error.message,
            url: req.url
          });
          
          // Open dialog only once
          if (!this.dialogOpen) {
            this.showAuthErrorDialog(error);
          }
        }
        
        // Re-throw error for component-level handling
        return throwError(() => error);
      })
    );
  }
  
  /**
   * Show authentication error dialog
   * Auto-redirects after 5 seconds
   */
  private showAuthErrorDialog(error: HttpErrorResponse): void {
    this.dialogOpen = true;
    
    const dialogData: AuthErrorDialogData = {
      statusCode: error.status,
      errorMessage: this.extractErrorMessage(error),
      redirectDelay: 5
    };
    
    const dialogRef = this.dialog.open(AuthErrorDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      disableClose: true,
      data: dialogData,
      panelClass: 'auth-error-dialog-container'
    });
    
    // Reset flag when dialog closes
    dialogRef.afterClosed().subscribe(() => {
      this.dialogOpen = false;
      console.log('[AuthErrorInterceptor] Dialog closed, redirecting to login...');
    });
  }
  
  /**
   * Extract user-friendly error message from HTTP response
   */
  private extractErrorMessage(error: HttpErrorResponse): string | undefined {
    // Check for custom error message in response body
    if (error.error?.message) {
      return error.error.message;
    }
    
    // Check for error text
    if (typeof error.error === 'string') {
      return error.error;
    }
    
    // Default messages based on status code
    switch (error.status) {
      case 401:
        return 'Your session has expired. Please log in again.';
      case 403:
        return 'You don\'t have permission to access this resource.';
      default:
        return undefined;
    }
  }
}

/* END OF FILE */
